import React, { useEffect, useState, useCallback } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../ui/sheet';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../ui/select';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Loader2, CheckCircle2, Factory, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { jobsApi, clientsApi, getErrorMessage } from '../../services/api';
import { formatINR } from '../command-center/constants';

const FALLBACK_INDUSTRIES = [
  'Technology', 'Finance', 'Healthcare', 'Manufacturing', 'Retail',
  'Education', 'Hospitality', 'Real Estate', 'Logistics', 'Other',
];

/**
 * Drawer: list every client that has at least one Joined application but
 * no `industry` set, with an inline dropdown to backfill in seconds.
 *
 * Triggered from JoinedByIndustryCard's hygiene warning. After each save
 * the row drops out optimistically; an `industry:backfilled` window event
 * pings parent components to refetch their counts.
 */
const QuickFixIndustryDrawer = ({ open, onOpenChange }) => {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [industries, setIndustries] = useState(FALLBACK_INDUSTRIES);
  const [drafts, setDrafts] = useState({});   // client_id -> chosen industry
  const [customs, setCustoms] = useState({}); // client_id -> custom industry text
  const [savingId, setSavingId] = useState(null);

  const refetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await jobsApi.unclassifiedClients();
      setItems(res.data?.clients || []);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Could not load unclassified clients'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    refetch();
    // Pull existing industry list to populate the dropdown so the values
    // stay consistent with what's already in the system.
    clientsApi.getIndustries()
      .then((res) => {
        const list = res.data?.industries || [];
        // Merge with fallback, dedupe, preserve casing of existing values.
        const merged = Array.from(new Set([...list, ...FALLBACK_INDUSTRIES])).filter(Boolean);
        setIndustries(merged);
      })
      .catch(() => { /* keep fallback list */ });
  }, [open, refetch]);

  const saveOne = async (clientId) => {
    const choice = (drafts[clientId] || '').trim();
    const custom = (customs[clientId] || '').trim();
    const industry = choice === '__custom__' ? custom : choice;
    if (!industry) {
      toast.error('Pick an industry first');
      return;
    }
    setSavingId(clientId);
    try {
      await jobsApi.patchClientIndustry(clientId, industry);
      toast.success(`Industry set to "${industry}"`);
      setItems((prev) => prev.filter((c) => c.id !== clientId));
      setDrafts((prev) => { const n = { ...prev }; delete n[clientId]; return n; });
      setCustoms((prev) => { const n = { ...prev }; delete n[clientId]; return n; });
      try {
        window.dispatchEvent(new CustomEvent('industry:backfilled', {
          detail: { client_id: clientId, industry },
        }));
      } catch { /* ignore */ }
      // If user just used a brand-new industry, push it onto the dropdown
      // for the remaining rows so they can re-select it.
      setIndustries((prev) => (prev.includes(industry) ? prev : [...prev, industry]));
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to set industry'));
    } finally {
      setSavingId(null);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto" data-testid="quick-fix-industry-drawer">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Factory className="w-4 h-4 text-amber-600" /> Quick-fix industry
          </SheetTitle>
          <SheetDescription>
            Clients with joined placements but no industry on file. Pick an
            industry from the list (or type your own) and Save — totals on
            the Industry chart update instantly.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4">
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-slate-500 py-8 justify-center">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : items.length === 0 ? (
            <div className="text-sm text-emerald-700 flex items-center gap-2 py-8 justify-center" data-testid="quick-fix-industry-empty">
              <CheckCircle2 className="w-4 h-4" /> Every client with a closure has an industry. Nice.
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {items.map((c) => {
                const saving = savingId === c.id;
                const choice = drafts[c.id] || '';
                const showCustom = choice === '__custom__';
                return (
                  <li
                    key={c.id}
                    className="py-3 space-y-2"
                    data-testid={`quick-fix-industry-row-${c.id}`}
                  >
                    <div className="flex items-baseline justify-between gap-3 flex-wrap">
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold text-slate-900 truncate" title={c.name}>{c.name}</div>
                        <div className="text-[11px] text-slate-500">
                          {c.joined_count} {c.joined_count === 1 ? 'closure' : 'closures'} · ₹{formatINR(c.joined_ctc) || '0'}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Select
                        value={choice}
                        onValueChange={(v) => setDrafts((prev) => ({ ...prev, [c.id]: v }))}
                      >
                        <SelectTrigger className="w-[200px] h-8 text-xs" data-testid={`quick-fix-industry-select-${c.id}`}>
                          <SelectValue placeholder="Choose industry…" />
                        </SelectTrigger>
                        <SelectContent>
                          {industries.map((ind) => (
                            <SelectItem key={ind} value={ind} className="text-xs">{ind}</SelectItem>
                          ))}
                          <SelectItem value="__custom__" className="text-xs">
                            <span className="inline-flex items-center gap-1"><Plus className="w-3 h-3" /> Add new…</span>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      {showCustom ? (
                        <Input
                          value={customs[c.id] || ''}
                          onChange={(e) => setCustoms((prev) => ({ ...prev, [c.id]: e.target.value }))}
                          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); saveOne(c.id); } }}
                          placeholder="New industry"
                          className="h-8 w-44 text-xs"
                          disabled={saving}
                          data-testid={`quick-fix-industry-custom-${c.id}`}
                        />
                      ) : null}
                      <Button
                        size="sm"
                        onClick={() => saveOne(c.id)}
                        disabled={saving || !choice || (showCustom && !(customs[c.id] || '').trim())}
                        className="h-8 px-3 bg-amber-600 hover:bg-amber-700 text-white"
                        data-testid={`quick-fix-industry-save-${c.id}`}
                      >
                        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Save'}
                      </Button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default QuickFixIndustryDrawer;
